import type { ChartDataRow } from '../../../types'
import type {
  ChartCardTableAccessorFnColumnDef,
  ChartCardTableAccessorKeyColumnDef,
  ChartCardTableColumn,
  ChartCardTableColumnDef
} from '../tanstack'

export const isColumnWithAccessorKey = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): column is ChartCardTableAccessorKeyColumnDef<DataKey, DataRow> =>
  'accessorKey' in column && typeof column.accessorKey === 'string'

export const isColumnWithAccessorFn = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): column is ChartCardTableAccessorFnColumnDef<DataKey, DataRow> =>
  'accessorFn' in column && typeof column.accessorFn === 'function'

const accessorKeyToColumnId = (accessorKey: string): string => accessorKey.replaceAll('.', '_')

export const getColumnId = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): string => {
  if (column.id != null) {
    return column.id
  }
  if (isColumnWithAccessorKey(column)) {
    return accessorKeyToColumnId(column.accessorKey as string)
  }
  if (typeof column.header === 'string') {
    return column.header
  }
  throw new Error(
    'ChartCard table column needs column.id, accessorKey, or a string header (same rules as @tanstack/react-table).'
  )
}

export const getColumnDataKey = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow> | ChartCardTableColumn<DataKey, DataRow>
): DataKey | undefined => {
  if ('columnDef' in column) {
    return getColumnDataKey(column.columnDef)
  }
  if (isColumnWithAccessorKey(column)) {
    return column.accessorKey as DataKey
  }
}
